import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import useTranslation from '../../../hooks/useTranslation';
import PortalDropdown from '../../../components/ui/PortalDropdown';
import StatusFilterDropdown from '../../../components/ui/StatusFilterDropdown';
import { DIVISION_OPTIONS } from '../../../constants/divisions';

/**
 * Filtres de l'onglet Directives : UN bouton « Filtres » (avec le nombre de
 * filtres actifs) qui ouvre un menu deroulant — statut, professionnel, division,
 * periode de reception, et « Reinitialiser ». Aucune rangee de filtres visible
 * en permanence.
 *
 * Les valeurs viennent de l'URL (voir ExtrasPage) et y retournent : elles
 * survivent au rechargement. Composant sans etat metier (seulement ouvert/ferme).
 */

// Points de couleur des statuts, memes tons que les pastilles du tableau.
const STATUS_DOTS = {
  pending: '#F59E0B',
  overdue: '#D97706',
  extra: '#16A34A',
  credit: '#DC2626',
  no_impact: '#9CA3AF',
};

// Nombre de filtres actifs ; la periode (du / au) compte pour un.
export const countActiveDirectiveFilters = (filters) =>
  [filters?.status, filters?.professional, filters?.division, filters?.from || filters?.to]?.filter(Boolean)?.length;

const DirectiveFiltersMenu = ({ filters, professionals = [], onChange, onReset }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const activeCount = countActiveDirectiveFilters(filters);

  const statusOptions = [
    { value: '', label: t('extras.directives.filters.allStatuses') },
    { value: 'pending', label: t('extras.directives.outcome.pending'), dot: STATUS_DOTS.pending },
    { value: 'overdue', label: t('extras.directives.filters.overdue'), dot: STATUS_DOTS.overdue },
    { value: 'extra', label: t('extras.directives.outcome.extra'), dot: STATUS_DOTS.extra },
    { value: 'credit', label: t('extras.directives.outcome.credit'), dot: STATUS_DOTS.credit },
    { value: 'no_impact', label: t('extras.directives.outcome.no_impact'), dot: STATUS_DOTS.no_impact },
  ];

  const professionalOptions = [
    { value: '', label: t('extras.directives.filters.allProfessionals') },
    ...professionals?.map((p) => ({ value: p?.id, label: p?.company_name || '—' })),
  ];

  const divisionOptions = [
    { value: '', label: t('extras.directives.filters.allDivisions') },
    ...DIVISION_OPTIONS?.map((d) => ({ value: d?.value, label: t(`extras.divisions.${d?.key}`) })),
  ];

  const dateClass =
    'h-8 w-full min-w-0 px-2 text-xs rounded-md border border-gray-200 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary';

  const row = (label, control) => (
    <div className="space-y-1">
      <p className="text-[10px] font-medium text-gray-500">{label}</p>
      {control}
    </div>
  );

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`inline-flex items-center gap-1.5 h-8 px-3 text-xs rounded-md border bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 ${
          activeCount > 0 ? 'border-primary text-primary' : 'border-gray-200 text-gray-700 hover:border-gray-300'
        }`}
      >
        <Icon name="SlidersHorizontal" size={13} />
        {activeCount > 0
          ? t('extras.directives.filters.buttonCount', { count: activeCount })
          : t('extras.directives.filters.button')}
        <Icon name="ChevronDown" size={12} className="text-gray-400" />
      </button>
      <PortalDropdown anchorRef={btnRef} open={open} onClose={() => setOpen(false)} align="left" width={300}>
        <div className="px-3 py-2.5 space-y-2.5">
          {row(t('extras.directives.filters.status'), (
            <StatusFilterDropdown options={statusOptions} value={filters?.status || ''} onChange={(v) => onChange?.({ status: v })} width={260} />
          ))}
          {row(t('extras.directives.filters.professional'), (
            <StatusFilterDropdown options={professionalOptions} value={filters?.professional || ''} onChange={(v) => onChange?.({ professional: v })} width={260} />
          ))}
          {row(t('extras.directives.filters.division'), (
            <StatusFilterDropdown options={divisionOptions} value={filters?.division || ''} onChange={(v) => onChange?.({ division: v })} width={260} />
          ))}
          {row(t('extras.directives.filters.period'), (
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-gray-500 flex-shrink-0">{t('extras.directives.filters.from')}</span>
              <input
                type="date"
                value={filters?.from || ''}
                max={filters?.to || undefined}
                onChange={(e) => onChange?.({ from: e?.target?.value })}
                className={dateClass}
                aria-label={`${t('extras.directives.filters.period')} — ${t('extras.directives.filters.from')}`}
              />
              <span className="text-[11px] text-gray-500 flex-shrink-0">{t('extras.directives.filters.to')}</span>
              <input
                type="date"
                value={filters?.to || ''}
                min={filters?.from || undefined}
                onChange={(e) => onChange?.({ to: e?.target?.value })}
                className={dateClass}
                aria-label={`${t('extras.directives.filters.period')} — ${t('extras.directives.filters.to')}`}
              />
            </div>
          ))}
          <div className="flex justify-end pt-1 border-t border-gray-100">
            <button
              type="button"
              onClick={onReset}
              disabled={activeCount === 0}
              className="inline-flex items-center gap-1 px-2 py-1 mt-1 text-[11px] font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <Icon name="RotateCcw" size={12} />
              {t('extras.directives.filters.reset')}
            </button>
          </div>
        </div>
      </PortalDropdown>
    </>
  );
};

export default DirectiveFiltersMenu;
